/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useContext } from 'react';
import type { SuperAppContext as BaseSuperAppContext } from '@airasia-rn/ui';

type SuperAppContextType = React.ContextType<typeof BaseSuperAppContext>;

export type AuthHandoffSession = {
  accessToken?: string | null;
  refreshToken?: string | null;
  userId?: string | null;
  loginMethod?: string;
  /**
  The super app reads it in AuthScreen.tsx: if isProfileComplete === false 
  it establishes the session but skips its own navigation, leaving the user 
  on the sign-up form.
   */
  isProfileComplete?: boolean;
};

export interface AccountNavigationHandlers {
  onProfilePress?: () => void;
  onCopyMemberIdPress?: () => void;
  onLogout?: () => void;
  onDeveloperOptionsPress?: () => void;
  onLanguageChange?: (languageId: string) => void;
  onCurrencyChange?: (currency: string) => void;
  onAuthSuccess?: (session: AuthHandoffSession) => void;
}

export interface AccountDisplayOptions {
  /** Hides the back button only on the main Account page. */
  hideMainAccountBackButton?: boolean;
  /**
   * Hides the whole header of the main Account page. Web hosts render their own
   * site header above it and the page is their base path, so there is nothing
   * to go back to and no second title to show.
   */
  hideMainAccountHeader?: boolean;
}

/**
 * Super app handlers passed in by the host, mirroring the Flights contract:
 * `backHandler` to return to the super app, `openModule` to hand a deeplink to
 * the host, and `deeplink` for the entry route. `backHandler`/`openModule` are
 * optional: when the host does not provide them (e.g. web / standalone) the
 * package falls back to its previous navigation behaviour.
 */
export type AccountContextValue = Pick<SuperAppContextType, 'deeplink'> &
  Partial<Pick<SuperAppContextType, 'backHandler' | 'openModule'>> &
  AccountNavigationHandlers &
  AccountDisplayOptions;

const AccountContext = React.createContext<AccountContextValue>({
  deeplink: '',
});

const useAccountContext = (): AccountContextValue => useContext(AccountContext);

export { AccountContext, useAccountContext };
